import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  MenuItem,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Typography
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import apiUrl from '../config';
import LayoutDashboard from '../layout/LayoutDashboard';
import { useAuth } from '../AuthContext';
import { NovaSectionLoader, NovaButtonLoader } from './ui/NovaLoader';
import '../CSS/Facture.css';

const emptyForm = {
  id_facture: null,
  id_utilisateur: '',
  numero_facture: '',
  nom_client: '',
  emetteur: '',
  date_facture: '',
  date_echeance: '',
  date_paiement: '',
  montant_ht: '',
  montant_ttc: '',
  devise: 'EUR',
  status: 'Non payée',
  file: null
};

const FacturePage = () => {
  const { role } = useAuth();

  const isAdmin =
    String(role || '').toUpperCase() === 'ADMIN';

  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);

  const [factures, setFactures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [filterStatus, setFilterStatus] = useState('Toutes');
  const [filterClient, setFilterClient] = useState('Tous');
  const [filterDevise, setFilterDevise] = useState('Toutes');
  const [filterEcheance, setFilterEcheance] = useState('Toutes');
  const [filterEmetteur, setFilterEmetteur] = useState('Tous');
  const [filterUtilisateurAttribue, setFilterUtilisateurAttribue] = useState('Tous');
  const [filterDatePaiement, setFilterDatePaiement] = useState('');
  const [filterSearch, setFilterSearch] = useState('');
  const [tablePage, setTablePage] = useState(0);
  const rowsPerPage = 50;

  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [factureToDelete, setFactureToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const [unpayDialogOpen, setUnpayDialogOpen] = useState(false);
  const [payDialogOpen, setPayDialogOpen] = useState(false);
  const [paymentDate, setPaymentDate] = useState('');
  const [factureToToggle, setFactureToToggle] = useState(null);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [formMode, setFormMode] = useState('add');
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [scanning, setScanning] = useState(false);

  const getToken = () => localStorage.getItem('token');

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${getToken()}` }
  });

  const normalizeFull = (value) => {
    if (!value) return '';

    return String(value)
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^\w\s]/g, '')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase()
      .split(' ')
      .sort()
      .join(' ');
  };

  const parseFrenchDateToInput = (value) => {
    if (!value) return '';

    const text = String(value).trim();

    if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return text;

    const match = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (!match) return '';

    const day = String(match[1]).padStart(2, '0');
    const month = String(match[2]).padStart(2, '0');
    const year = match[3];

    return `${year}-${month}-${day}`;
  };

  const parseFactureDateForDelay = (value) => {
    const inputValue = parseFrenchDateToInput(value);

    if (!inputValue) return null;

    const date = new Date(`${inputValue}T00:00:00`);

    if (Number.isNaN(date.getTime())) return null;

    return date;
  };

  const isFactureOverdue = (facture) => {
    if (!facture || facture.status === 'Payée') return false;

    const dueDate = parseFactureDateForDelay(facture.date_echeance);

    if (!dueDate) return false;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return dueDate < today;
  };

  const getDevise = (facture) => String(facture?.devise || 'EUR').toUpperCase();

  const formatAmount = (value, devise = 'EUR') => {
    const num = Number(String(value ?? '0').replace(',', '.'));

    if (!Number.isFinite(num)) return '—';

    if (String(devise).toUpperCase() === 'MAD') {
      return `${num.toLocaleString('fr-FR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })} DH`;
    }

    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(num);
  };
  const fetchAssignableUsers = useCallback(async () => {
    if (!isAdmin) {
      setUsers([]);
      return;
    }

    setUsersLoading(true);

    try {
      const token = localStorage.getItem('token');

      const response = await axios.get(
        `${apiUrl}/users`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      const rawUsers = Array.isArray(response.data)
        ? response.data
        : [];

      const assignableUsers = rawUsers
        .map((user) => {
          const userId =
            user.id_utilisateur ??
            user.Id_Utilisateur ??
            user.id ??
            user.user_id;

          const userRole = String(
            user.role ??
            user.type ??
            user.Type ??
            ''
          ).toUpperCase();

          const active =
            Number(user.compte_actif ?? 1) === 1;

          return {
            ...user,
            id_utilisateur: userId,
            normalized_role: userRole,
            normalized_active: active
          };
        })
        .filter((user) => (
          user.id_utilisateur &&
          user.normalized_role === 'USER' &&
          user.normalized_active
        ))
        .sort((firstUser, secondUser) => {
          const firstName = `${
            firstUser.prenom ||
            firstUser.Prenom ||
            ''
          } ${
            firstUser.nom ||
            firstUser.Nom ||
            ''
          }`.trim();

          const secondName = `${
            secondUser.prenom ||
            secondUser.Prenom ||
            ''
          } ${
            secondUser.nom ||
            secondUser.Nom ||
            ''
          }`.trim();

          return firstName.localeCompare(
            secondName,
            'fr',
            { sensitivity: 'base' }
          );
        });

      setUsers(assignableUsers);

    } catch (error) {
      console.error(
        'Erreur chargement utilisateurs :',
        error
      );

      setUsers([]);

      setNotification({
        open: true,
        message:
          error.response?.data?.message ||
          "Impossible de charger les utilisateurs.",
        severity: 'error'
      });

    } finally {
      setUsersLoading(false);
    }
  }, [isAdmin]);



  const fetchFactures = useCallback(async (options = {}) => {
    const silent = Boolean(options.silent);

    if (silent) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    try {
      const response = await axios.get(`${apiUrl}/facturebiam`, getAuthHeaders());
      setFactures(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Erreur chargement factures:', error);

      if (!silent) {
        setNotification({
          open: true,
          message: 'Erreur lors du chargement des factures.',
          severity: 'error'
        });
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);
  useEffect(() => {
    fetchAssignableUsers();
  }, [fetchAssignableUsers]);


  useEffect(() => {
    fetchFactures();
  }, [fetchFactures]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (!formDialogOpen && !deleteDialogOpen && !unpayDialogOpen && !payDialogOpen && !saving && !isDeleting && !isUpdatingStatus) {
        fetchFactures({ silent: true });
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [formDialogOpen, deleteDialogOpen, unpayDialogOpen, payDialogOpen, saving, isDeleting, isUpdatingStatus, fetchFactures]);

  useEffect(() => {
    setTablePage(0);
  }, [filterStatus, filterClient, filterDevise, filterEcheance, filterEmetteur, filterDatePaiement, filterSearch]);

  // ===== ETHIK FACTURE DROPDOWN ANCHOR FINAL START =====

  /*
    Les listes des filtres restent coll?es
    ? leur champ pendant le scroll.

    Tant que le champ reste visible :
      menu ouvert + position synchronis?e.

    Quand le champ sort compl?tement
    de la zone visible :
      menu ferm?.
  */

  const [
    openFactureFilterMenu,
    setOpenFactureFilterMenu
  ] = React.useState(null);


  const factureFilterAnchorRef =
    React.useRef(null);


  const getFactureFilterSelectProps =
    React.useCallback(
      (menuKey) => ({

        open:
          openFactureFilterMenu === menuKey,

        onOpen: (event) => {

          const current =
            event?.currentTarget || null;

          const anchor =
            current?.closest?.(
              '.MuiInputBase-root'
            )
            || current;

          factureFilterAnchorRef.current =
            anchor;

          setOpenFactureFilterMenu(
            menuKey
          );
        },

        onClose: () => {

          setOpenFactureFilterMenu(
            null
          );

          factureFilterAnchorRef.current =
            null;
        },

        MenuProps: {

          disableScrollLock: true,

          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left'
          },

          transformOrigin: {
            vertical: 'top',
            horizontal: 'left'
          },

          PaperProps: {

            className:
              'facture-filter-menu-paper',

            sx: {
              maxHeight: 340,
              overflowY: 'auto'
            }
          }
        }
      }),
      [
        openFactureFilterMenu
      ]
    );


  React.useEffect(() => {

    if (!openFactureFilterMenu) {
      return undefined;
    }


    let animationFrameId = null;


    const anchorIsVisible = (
      anchor
    ) => {

      if (
        !anchor
        ||
        !anchor.isConnected
      ) {
        return false;
      }


      const rect =
        anchor.getBoundingClientRect();


      if (
        rect.width <= 0
        ||
        rect.height <= 0
      ) {
        return false;
      }


      /*
        Hors viewport navigateur.
      */
      if (
        rect.bottom <= 0
        ||
        rect.top >= window.innerHeight
        ||
        rect.right <= 0
        ||
        rect.left >= window.innerWidth
      ) {
        return false;
      }


      /*
        V?rifie aussi les conteneurs scrollables
        du dashboard.

        Si le champ est cach? par le bord
        d'un de ces conteneurs, le menu dispara?t.
      */
      let parent =
        anchor.parentElement;


      while (
        parent
        &&
        parent !== document.body
        &&
        parent !== document.documentElement
      ) {

        const style =
          window.getComputedStyle(
            parent
          );


        const overflowY =
          style.overflowY;

        const overflowX =
          style.overflowX;


        const clipsY =
          (
            overflowY === 'auto'
            ||
            overflowY === 'scroll'
            ||
            overflowY === 'hidden'
            ||
            overflowY === 'clip'
          );


        const clipsX =
          (
            overflowX === 'auto'
            ||
            overflowX === 'scroll'
            ||
            overflowX === 'hidden'
            ||
            overflowX === 'clip'
          );


        if (
          clipsY
          ||
          clipsX
        ) {

          const parentRect =
            parent.getBoundingClientRect();


          if (
            clipsY
            &&
            (
              rect.bottom <= parentRect.top
              ||
              rect.top >= parentRect.bottom
            )
          ) {
            return false;
          }


          if (
            clipsX
            &&
            (
              rect.right <= parentRect.left
              ||
              rect.left >= parentRect.right
            )
          ) {
            return false;
          }
        }


        parent =
          parent.parentElement;
      }


      return true;
    };


    const synchroniseMenu = () => {

      const anchor =
        factureFilterAnchorRef.current;


      if (
        !anchorIsVisible(
          anchor
        )
      ) {

        setOpenFactureFilterMenu(
          null
        );

        factureFilterAnchorRef.current =
          null;

        return;
      }


      const paper =
        document.querySelector(
          '.facture-filter-menu-paper'
        );


      if (paper) {

        const rect =
          anchor.getBoundingClientRect();


        const gap = 4;


        const paperWidth =
          paper.offsetWidth || rect.width;


        const paperHeight =
          paper.offsetHeight || 200;


        const viewportPadding = 8;


        const spaceBelow =
          window.innerHeight
          - rect.bottom
          - viewportPadding;


        const spaceAbove =
          rect.top
          - viewportPadding;


        /*
          Comportement SaaS :
          normalement sous le champ.

          Si plus assez de place dessous
          mais plus de place au-dessus,
          le menu passe au-dessus.
        */

        let top;
        let maxHeight;


        if (
          spaceBelow >= 140
          ||
          spaceBelow >= spaceAbove
        ) {

          top =
            rect.bottom
            + gap;

          maxHeight =
            Math.max(
              80,
              Math.min(
                340,
                spaceBelow - gap
              )
            );

        } else {

          maxHeight =
            Math.max(
              80,
              Math.min(
                340,
                spaceAbove - gap
              )
            );


          const effectiveHeight =
            Math.min(
              paperHeight,
              maxHeight
            );


          top =
            rect.top
            - effectiveHeight
            - gap;
        }


        const maxLeft =
          Math.max(
            viewportPadding,
            window.innerWidth
            - paperWidth
            - viewportPadding
          );


        const left =
          Math.min(
            Math.max(
              viewportPadding,
              rect.left
            ),
            maxLeft
          );


        /*
          Le Paper du Popover est positionn?
          par rapport au viewport.

          On force sa position r?elle ? suivre
          le champ ? chaque frame.
        */

        paper.style.setProperty(
          'top',
          `${Math.round(top)}px`
        );

        paper.style.setProperty(
          'left',
          `${Math.round(left)}px`
        );

        paper.style.setProperty(
          'right',
          'auto'
        );

        paper.style.setProperty(
          'margin-top',
          '0px'
        );

        paper.style.setProperty(
          'min-width',
          `${Math.round(rect.width)}px`
        );

        paper.style.setProperty(
          'max-height',
          `${Math.round(maxHeight)}px`
        );

        paper.style.setProperty(
          'transform-origin',
          'top left'
        );
      }


      /*
        Synchronisation continue uniquement
        pendant qu'un menu est ouvert.

        Ainsi le d?placement reste parfaitement
        fluide m?me avec trackpad / molette rapide.
      */

      animationFrameId =
        window.requestAnimationFrame(
          synchroniseMenu
        );
    };


    animationFrameId =
      window.requestAnimationFrame(
        synchroniseMenu
      );


    return () => {

      if (
        animationFrameId !== null
      ) {

        window.cancelAnimationFrame(
          animationFrameId
        );
      }
    };

  }, [
    openFactureFilterMenu
  ]);

  // ===== ETHIK FACTURE DROPDOWN ANCHOR FINAL END =====

  const clientNames = useMemo(() => {
    const uniqueClientsMap = new Map();

    factures.forEach((facture) => {
      const original = facture.nom_client;
      const normalized = normalizeFull(original);

      if (normalized && !uniqueClientsMap.has(normalized)) {
        uniqueClientsMap.set(normalized, original?.trim() || '');
      }
    });

    return Array.from(uniqueClientsMap.entries()).sort((a, b) =>
      a[1].localeCompare(b[1], 'fr', { sensitivity: 'base' })
    );
  }, [factures]);

  const emetteurNames = useMemo(() => {
    const unique = new Map();

    factures.forEach((facture) => {
      const original = facture.emetteur;
      const normalized = normalizeFull(original);

      if (
        normalized
        && !unique.has(normalized)
      ) {
        unique.set(
          normalized,
          original?.trim() || ''
        );
      }
    });

    return Array.from(
      unique.entries()
    ).sort((a, b) =>
      a[1].localeCompare(
        b[1],
        'fr',
        { sensitivity: 'base' }
      )
    );
  }, [factures]);

  const filteredFactures = useMemo(() => {
    let filtered = factures;

    if (filterStatus !== 'Toutes') {
      filtered = filtered.filter((facture) => facture.status === filterStatus);
    }

    if (filterClient !== 'Tous') {
      filtered = filtered.filter((facture) => normalizeFull(facture.nom_client) === filterClient);
    }

    if (filterEmetteur !== 'Tous') {
      filtered = filtered.filter(
        (facture) =>
          normalizeFull(facture.emetteur)
          === filterEmetteur
      );
    }

    if (filterDatePaiement) {
      filtered = filtered.filter(
        (facture) =>
          parseFrenchDateToInput(
            facture.date_paiement
          ) === filterDatePaiement
      );
    }


    if (filterDevise !== 'Toutes') {
      filtered = filtered.filter((facture) => getDevise(facture) === filterDevise);
    }

    if (filterEcheance === 'En retard') {
      filtered = filtered.filter((facture) => isFactureOverdue(facture));
    }

    if (filterEcheance === 'Non échues') {
      filtered = filtered.filter((facture) => !isFactureOverdue(facture));
    }

    const search = filterSearch.trim().toLowerCase();

    if (search) {
      filtered = filtered.filter((facture) => {
        return [
          facture.numero_facture,
          facture.nom_client,
          facture.emetteur,
          facture.utilisateur_nom,
          facture.utilisateur_email,
          facture.date_facture,
          facture.date_echeance,
          facture.date_paiement,
          facture.status,
          facture.devise
        ].some((value) => String(value || '').toLowerCase().includes(search));
      });
    }

    if (
      filterUtilisateurAttribue !== 'Tous'
    ) {
      const selectedUserId =
        String(filterUtilisateurAttribue);

      filtered = filtered.filter(
        (facture) => {
          const factureUserId =
            facture.id_utilisateur ??
            facture.Id_Utilisateur ??
            facture.user_id ??
            '';

          return (
            String(factureUserId)
            === selectedUserId
          );
        }
      );
    }

    return filtered;
  }, [factures, filterStatus, filterClient, filterDevise, filterEcheance, filterEmetteur, filterDatePaiement, filterSearch, filterUtilisateurAttribue]);

  const stats = useMemo(() => {
    const total = factures.length;
    const payees = factures.filter((facture) => facture.status === 'Payée').length;
    const nonPayees = factures.filter((facture) => facture.status !== 'Payée').length;
    const overdue = factures.filter((facture) => isFactureOverdue(facture)).length;

    const facturesEUR = factures.filter((facture) => getDevise(facture) === 'EUR');
    const facturesMAD = factures.filter((facture) => getDevise(facture) === 'MAD');

    const countEUR = facturesEUR.length;
    const countMAD = facturesMAD.length;

    const totalEUR = facturesEUR.reduce((sum, facture) => {
      return sum + Number(String(facture.montant_ttc || '0').replace(',', '.'));
    }, 0);

    const totalMAD = facturesMAD.reduce((sum, facture) => {
      return sum + Number(String(facture.montant_ttc || '0').replace(',', '.'));
    }, 0);

    return { total, payees, nonPayees, overdue, countEUR, countMAD, totalEUR, totalMAD };
  }, [factures]);

  const paginatedFactures = useMemo(() => {
    const start = tablePage * rowsPerPage;
    return filteredFactures.slice(start, start + rowsPerPage);
  }, [filteredFactures, tablePage, rowsPerPage]);

  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(filteredFactures.length / rowsPerPage) - 1);

    if (tablePage > maxPage) {
      setTablePage(maxPage);
    }
  }, [filteredFactures.length, tablePage, rowsPerPage]);

  const resetFilters = () => {
    setFilterStatus('Toutes');
    setFilterClient('Tous');
    setFilterDevise('Toutes');
    setFilterEcheance('Toutes');
    setFilterEmetteur('Tous');
    setFilterUtilisateurAttribue('Tous');
    setFilterDatePaiement('');
    setFilterSearch('');
  };

  const resetForm = () => {
    setForm(emptyForm);
  };

  const updateForm = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const openAddDialog = () => {
    setFormMode('add');
    resetForm();
    setFormDialogOpen(true);
  };

  const openEditDialog = (facture) => {
    setFormMode('edit');

    setForm({
      id_facture: facture.id_facture,
      id_utilisateur:
        facture.id_utilisateur ??
        facture.Id_Utilisateur ??
        '',
      numero_facture: facture.numero_facture || '',
      nom_client: facture.nom_client || '',
      emetteur: facture.emetteur === '—' ? '' : facture.emetteur || '',
      date_facture: parseFrenchDateToInput(facture.date_facture),
      date_echeance: parseFrenchDateToInput(facture.date_echeance),
      date_paiement: parseFrenchDateToInput(facture.date_paiement),
      montant_ht: facture.montant_ht ?? '',
      montant_ttc: facture.montant_ttc ?? '',
      devise: getDevise(facture),
      status: facture.status || 'Non payée',
      file: null
    });

    setFormDialogOpen(true);
  };

  const handlePdfSelected = async (event) => {
    const file = event.target.files?.[0] || null;

    if (!file) return;

    setForm((current) => ({
      ...current,
      file
    }));

    // En modification, on remplace seulement le PDF.
    // Le scan automatique est r?serv? ? l'ajout.
    if (formMode !== 'add') return;

    if (!file.name?.toLowerCase().endsWith('.pdf')) {
      setNotification({
        open: true,
        message: 'Veuillez choisir un fichier PDF.',
        severity: 'error'
      });
      return;
    }

    setScanning(true);

    try {
      const scanData = new FormData();
      scanData.append('facture_pdf', file);

      const response = await axios.post(
        `${apiUrl}/facturebiam/analyser`,
        scanData,
        {
          headers: {
            Authorization: `Bearer ${getToken()}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      const detected = response.data?.data || {};

      setForm((current) => ({
        ...current,
        file,
        numero_facture:
          detected.numero_facture || current.numero_facture,
        nom_client:
          detected.nom_client || current.nom_client,
        emetteur:
          detected.emetteur || current.emetteur,
        date_facture:
          parseFrenchDateToInput(detected.date_facture)
          || current.date_facture,
        date_echeance:
          parseFrenchDateToInput(detected.date_echeance)
          || current.date_echeance,
        montant_ht:
          detected.montant_ht || current.montant_ht,
        montant_ttc:
          detected.montant_ttc || current.montant_ttc,
        devise:
          detected.devise || current.devise
      }));

      const warnings = Array.isArray(
        response.data?.warnings
      )
        ? response.data.warnings
        : [];

      setNotification({
        open: true,
        message:
          warnings.length > 0
            ? `Facture analys\u00e9e. ${warnings.join(' ')}`
            : 'Facture analys\u00e9e : le formulaire a \u00e9t\u00e9 pr\u00e9rempli. V\u00e9rifiez les informations avant de l\u2019ajouter.',
        severity: warnings.length > 0 ? 'warning' : 'success'
      });

    } catch (error) {
      console.error(
        'Erreur analyse facture:',
        error.response?.data || error
      );

      setNotification({
        open: true,
        message:
          error.response?.data?.error
          || 'Impossible d\u2019analyser automatiquement cette facture.',
        severity: 'error'
      });

    } finally {
      setScanning(false);
    }
  };

  const validateForm = () => {
    if (!form.id_utilisateur) {
      setNotification({
        open: true,
        message: 'Veuillez s\u00e9lectionner un utilisateur attribu\u00e9.',
        severity: 'error'
      });

      return false;
    }

    if (
      form.status === 'Pay\u00e9e'
      && !form.date_paiement
    ) {
      setNotification({
        open: true,
        message: 'La date de paiement est obligatoire pour une facture pay\u00e9e.',
        severity: 'error'
      });

      return false;
    }

    if (
      formMode === 'add'
      && !form.file
    ) {
      setNotification({
        open: true,
        message: 'Veuillez s\u00e9lectionner le PDF de la facture.',
        severity: 'error'
      });

      return false;
    }

    return true;
  };

  const handleSaveFacture = async () => {
    if (!validateForm()) return;

    setSaving(true);

    try {
      const formData = new FormData();

      if (form.file) {
        formData.append('facture_pdf', form.file);
      }
      formData.append(
        'Id_Utilisateur',
        String(form.id_utilisateur)
      );


      formData.append('date_facture', form.date_facture);
      formData.append('date_echeance', form.date_echeance);
      formData.append('status', form.status);
      formData.append('date_paiement', form.status === 'Pay\u00e9e' ? form.date_paiement : '');
      formData.append('nom_client', form.nom_client);
      formData.append('emetteur', form.emetteur);
      formData.append('montant_ht', form.montant_ht);
      formData.append('montant_ttc', form.montant_ttc);
      formData.append('numero_facture', form.numero_facture);
      formData.append('devise', form.devise);

      if (formMode === 'edit') {
        await axios.put(`${apiUrl}/facturebiam/${form.id_facture}`, formData, {
          headers: {
            Authorization: `Bearer ${getToken()}`,
            'Content-Type': 'multipart/form-data'
          }
        });

        setNotification({
          open: true,
          message: 'Facture modifiée avec succès.',
          severity: 'success'
        });
      } else {
        await axios.post(`${apiUrl}/facturebiam/ajouter`, formData, {
          headers: {
            Authorization: `Bearer ${getToken()}`,
            'Content-Type': 'multipart/form-data'
          }
        });

        setNotification({
          open: true,
          message: 'Facture ajoutée avec succès.',
          severity: 'success'
        });
      }

      setFormDialogOpen(false);
      resetForm();

      await fetchFactures({ silent: true });
    } catch (error) {
      console.error('Erreur sauvegarde facture:', error.response?.data || error);

      setNotification({
        open: true,
        message: error.response?.data?.error || error.response?.data?.message || "Erreur lors de l'enregistrement de la facture.",
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = async (factureId) => {
    try {
      const response = await axios.get(`${apiUrl}/facturebiam/${factureId}/telecharger`, {
        ...getAuthHeaders(),
        responseType: 'blob'
      });

      const urlBlob = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');

      link.href = urlBlob;
      link.setAttribute('download', `facture_${factureId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(urlBlob);
    } catch (error) {
      console.error('Erreur téléchargement facture:', error);
      setNotification({
        open: true,
        message: 'Erreur lors du téléchargement de la facture.',
        severity: 'error'
      });
    }
  };

  const updateStatus = async (
    factureId,
    newStatus,
    datePaiement = null
  ) => {
    if (!factureId) return;

    if (
      newStatus === 'Pay\u00e9e'
      && !datePaiement
    ) {
      setNotification({
        open: true,
        message: 'Veuillez renseigner la date de paiement.',
        severity: 'error'
      });

      return;
    }

    setIsUpdatingStatus(true);

    try {
      await axios.put(
        `${apiUrl}/facturebiam/status/${factureId}`,
        {
          status: newStatus,
          date_paiement:
            newStatus === 'Pay\u00e9e'
              ? datePaiement
              : null
        },
        getAuthHeaders()
      );

      setNotification({
        open: true,
        message:
          newStatus === 'Pay\u00e9e'
            ? 'Facture marqu\u00e9e comme pay\u00e9e.'
            : 'Facture remise en non pay\u00e9e.',
        severity: 'success'
      });

      await fetchFactures({
        silent: true
      });

    } catch (error) {
      console.error(
        'Erreur statut facture:',
        error
      );

      setNotification({
        open: true,
        message:
          error.response?.data?.message
          || error.response?.data?.error
          || 'Erreur lors de la mise \u00e0 jour du statut.',
        severity: 'error'
      });

    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleDeleteFacture = async () => {
    if (!factureToDelete) return;

    setIsDeleting(true);

    try {
      await axios.delete(`${apiUrl}/facturebiam/${factureToDelete.id_facture}`, getAuthHeaders());

      setNotification({
        open: true,
        message: 'Facture supprimée avec succès.',
        severity: 'success'
      });

      setDeleteDialogOpen(false);
      setFactureToDelete(null);

      await fetchFactures({ silent: true });
    } catch (error) {
      console.error('Erreur suppression facture:', error);
      setNotification({
        open: true,
        message: 'Erreur lors de la suppression de la facture.',
        severity: 'error'
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <LayoutDashboard>
      <main className="factures-page">
        <section className="factures-hero">
          <div className="factures-hero-text">
            <span className="factures-pill">Gestion financière</span>
            <h1>Factures</h1>
            <p>
              {isAdmin
                ? 'Ajoutez, attribuez, modifiez, téléchargez et suivez toutes les factures.'
                : 'Consultez et téléchargez uniquement les factures qui vous sont attribuées.'}
            </p>
          </div>

          <Stack className="factures-hero-actions" direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <Button
              className="factures-refresh-btn"
              variant="outlined"
              startIcon={refreshing ? null : <RefreshRoundedIcon />}
              disabled={refreshing}
              onClick={() => fetchFactures({ silent: true })}
            >
              {refreshing ? 'Actualisation...' : 'Rafraîchir'}
            </Button>

            {isAdmin && (
              <Button
              className="factures-add-btn"
              variant="contained"
              startIcon={<AddRoundedIcon />}
              onClick={openAddDialog}
            >
              Ajouter une facture
            </Button>
            )}
          </Stack>
        </section>

        <Container maxWidth={false} disableGutters className="factures-container-modern">
          <section className="factures-stats-grid">
            <article className="factures-stat-card">
              <span>Total</span>
              <strong>{stats.total}</strong>
              <small>facture(s)</small>
            </article>

            <article className="factures-stat-card">
              <span>Payées</span>
              <strong>{stats.payees}</strong>
              <small>facture(s)</small>
            </article>

            <article className="factures-stat-card">
              <span>Non payées</span>
              <strong>{stats.nonPayees}</strong>
              <small>à suivre</small>
            </article>

            <article className="factures-stat-card facture-stat-overdue">
              <span>En retard</span>
              <strong>{stats.overdue}</strong>
              <small>échéance dépassée</small>
            </article>

            <article className="factures-stat-card">
              <span>Factures €</span>
              <strong>{stats.countEUR}</strong>
              <small>{formatAmount(stats.totalEUR, 'EUR')} TTC</small>
            </article>

            <article className="factures-stat-card">
              <span>Factures DH</span>
              <strong>{stats.countMAD}</strong>
              <small>{formatAmount(stats.totalMAD, 'MAD')} TTC</small>
            </article>
          </section>

          <section className="factures-toolbar-card">
            <div className="factures-toolbar-title">
              <Typography className="factures-section-title">Liste des factures</Typography>
              <Typography className="factures-section-helper">
                {filteredFactures.length} facture(s) affichée(s) sur {factures.length}
              </Typography>
            </div>

            <div className="factures-filters">
              <FormControl size="small">
                <TextField
                  select
                  label="Statut"
                  SelectProps={getFactureFilterSelectProps('statut')}
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                >
                  <MenuItem value="Toutes">Toutes</MenuItem>
                  <MenuItem value="Payée">Payée</MenuItem>
                  <MenuItem value="Non payée">Non payée</MenuItem>
                </TextField>
              </FormControl>

              <FormControl size="small">
                <TextField
                  select
                  label="Client"
                  SelectProps={getFactureFilterSelectProps('client')}
                  value={filterClient}
                  onChange={(e) => setFilterClient(e.target.value)}
                >
                  <MenuItem value="Tous">Tous</MenuItem>
                  {clientNames.map(([key, original]) => (
                    <MenuItem key={key} value={key}>{original}</MenuItem>
                  ))}
                </TextField>
              </FormControl>

              <FormControl size="small">
                <TextField
                  select
                  label={'\u00c9metteur'}
                  SelectProps={getFactureFilterSelectProps('emetteur')}
                  value={filterEmetteur}
                  onChange={(e) =>
                    setFilterEmetteur(
                      e.target.value
                    )
                  }
                >
                  <MenuItem value="Tous">
                    Tous
                  </MenuItem>

                  {emetteurNames.map(
                    ([key, original]) => (
                      <MenuItem
                        key={key}
                        value={key}
                      >
                        {original}
                      </MenuItem>
                    )
                  )}
                </TextField>
              </FormControl>

              <FormControl size="small">
                <TextField
                  select
                  label="Devise"
                  SelectProps={getFactureFilterSelectProps('devise')}
                  value={filterDevise}
                  onChange={(e) => setFilterDevise(e.target.value)}
                >
                  <MenuItem value="Toutes">Toutes</MenuItem>
                  <MenuItem value="EUR">Euro (€)</MenuItem>
                  <MenuItem value="MAD">Dirham marocain (DH)</MenuItem>
                </TextField>
              </FormControl>

              <FormControl size="small">
                <TextField
                  select
                  label="Échéance"
                  SelectProps={getFactureFilterSelectProps('echeance')}
                  value={filterEcheance}
                  onChange={(e) => setFilterEcheance(e.target.value)}
                >
                  <MenuItem value="Toutes">Toutes</MenuItem>
                  <MenuItem value="En retard">En retard</MenuItem>
                  <MenuItem value="Non échues">Non échues</MenuItem>
                </TextField>
              </FormControl>

                            <TextField
                size="small"
                label="Date de paiement"
                type="date"
                value={filterDatePaiement}
                onChange={(e) =>
                  setFilterDatePaiement(
                    e.target.value
                  )
                }
                InputLabelProps={{
                  shrink: true
                }}
              />

              {isAdmin && (
                <FormControl size="small">
                  <TextField
                    select
                    size="small"
                    label={'Personne attribu\u00e9e'}
                    SelectProps={getFactureFilterSelectProps('utilisateur')}
                    value={filterUtilisateurAttribue}
                    onChange={(event) => {
                      setFilterUtilisateurAttribue(
                        event.target.value
                      );
                      setTablePage(0);
                    }}
                    disabled={usersLoading}
                  >
                    <MenuItem value="Tous">
                      Toutes les personnes
                    </MenuItem>

                    {users.map((user) => {
                      const userId =
                        user.id_utilisateur ??
                        user.Id_Utilisateur ??
                        user.id ??
                        user.user_id;

                      const prenom =
                        user.prenom ??
                        user.Prenom ??
                        '';

                      const nom =
                        user.nom ??
                        user.Nom ??
                        '';

                      const userLabel =
                        `${prenom} ${nom}`.trim();

                      if (
                        userId === undefined ||
                        userId === null ||
                        userId === ''
                      ) {
                        return null;
                      }

                      return (
                        <MenuItem
                          key={String(userId)}
                          value={String(userId)}
                        >
                          {
                            userLabel ||
                            `Utilisateur ${userId}`
                          }
                        </MenuItem>
                      );
                    })}
                  </TextField>
                </FormControl>
              )}

<TextField
                size="small"
                label="Rechercher une facture"
                placeholder={'N\u00b0, client, \u00e9metteur...'}
                value={filterSearch}
                onChange={(e) => setFilterSearch(e.target.value)}
              />

              <Button className="factures-reset-btn" variant="outlined" onClick={resetFilters}>
                Réinitialiser
              </Button>
            </div>
          </section>

          <section className="factures-table-card">
            {loading ? (
              <NovaSectionLoader label="Chargement des factures" helper="Merci de patienter..." />
            ) : (
              <TableContainer className="factures-table-container">
                <Table className="factures-table">
                  <TableHead>
                    <TableRow>
                      <TableCell>Client</TableCell>
                      <TableCell>N° facture</TableCell>
                      <TableCell>Date de facture</TableCell>
                      <TableCell>Échéance</TableCell>
                      <TableCell>{"\u00c9metteur"}</TableCell>
                      <TableCell>Date paiement</TableCell>
                      {isAdmin && (
                        <TableCell>
                          Utilisateur attribué
                        </TableCell>
                      )}
                      <TableCell align="right">HT</TableCell>
                      <TableCell align="right">TTC</TableCell>
                      <TableCell align="center">Devise</TableCell>
                      <TableCell align="center">Statut</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {paginatedFactures.map((facture) => {
                      const overdue = isFactureOverdue(facture);
                      const devise = getDevise(facture);

                      return (
                        <TableRow key={facture.id_facture}>
                          <TableCell>
                            <div className="facture-client-cell">
                              <ReceiptLongRoundedIcon />
                              <span>{facture.nom_client || 'Sans client'}</span>
                            </div>
                          </TableCell>

                          <TableCell>{facture.numero_facture || '—'}</TableCell>
                          <TableCell>{facture.date_facture || '—'}</TableCell>
                          <TableCell>{facture.date_echeance || '—'}</TableCell>
                          <TableCell>{facture.emetteur || '—'}</TableCell>
                          <TableCell>
                            {facture.date_paiement || '\u2014'}
                          </TableCell>

                          {isAdmin && (
                            <TableCell>
                              <div>
                                <strong>
                                  {
                                    facture.utilisateur_nom ||
                                    'Utilisateur'
                                  }
                                </strong>

                                {
                                  facture.utilisateur_email &&
                                  (
                                    <Typography
                                      component="div"
                                      variant="caption"
                                    >
                                      {
                                        facture.utilisateur_email
                                      }
                                    </Typography>
                                  )
                                }
                              </div>
                            </TableCell>
                          )}

                          <TableCell align="right">{formatAmount(facture.montant_ht, devise)}</TableCell>

                          <TableCell align="right">
                            <strong>{formatAmount(facture.montant_ttc, devise)}</strong>
                          </TableCell>

                          <TableCell align="center">
                            <Chip
                              size="small"
                              label={devise === 'MAD' ? 'DH' : '€'}
                              className={devise === 'MAD' ? 'facture-devise-chip mad' : 'facture-devise-chip eur'}
                            />
                          </TableCell>

                          <TableCell align="center">
                            <Chip
                              label={overdue ? 'En retard' : facture.status || 'Inconnu'}
                              size="small"
                              className={
                                facture.status === 'Payée'
                                  ? 'facture-status-chip paid'
                                  : overdue
                                    ? 'facture-status-chip overdue'
                                    : 'facture-status-chip unpaid'
                              }
                            />
                          </TableCell>

                          <TableCell align="center">
                            <div className="facture-actions-cell">
                              <Button
                                className="facture-mini-btn download"
                                onClick={() => handleDownload(facture.id_facture)}
                              >
                                <DownloadRoundedIcon />
                              </Button>
                              {isAdmin && (
                                <>


                              <Button
                                className="facture-mini-btn edit"
                                onClick={() => openEditDialog(facture)}
                              >
                                <EditRoundedIcon />
                              </Button>

                              <Button
                                className={facture.status === 'Payée' ? 'facture-mini-btn pay-action is-paid' : 'facture-mini-btn pay-action is-unpaid'}
                                disabled={isUpdatingStatus}
                                onClick={() => {
                                  if (facture.status === 'Payée') {
                                    setFactureToToggle(facture);
                                    setUnpayDialogOpen(true);
                                  } else {
                                    setFactureToToggle(facture);
                                    setPaymentDate('');
                                    setPayDialogOpen(true);
                                  }
                                }}
                              >
                                <CheckCircleRoundedIcon />
                              </Button>

                              <Button
                                className="facture-mini-btn delete"
                                onClick={() => {
                                  setFactureToDelete(facture);
                                  setDeleteDialogOpen(true);
                                }}
                              >
                                <DeleteRoundedIcon />
                              </Button>
                                </>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}

                    {filteredFactures.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={isAdmin ? 12 : 11}>
                          <div className="factures-empty-state">
                            Aucune facture disponible pour ces filtres.
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>

                <TablePagination
                  component="div"
                  count={filteredFactures.length}
                  page={tablePage}
                  onPageChange={(event, newPage) => setTablePage(newPage)}
                  rowsPerPage={rowsPerPage}
                  rowsPerPageOptions={[50]}
                  labelRowsPerPage="Lignes par page"
                  labelDisplayedRows={({ from, to, count }) =>
                    `${from}-${to} sur ${count !== -1 ? count : `plus de ${to}`}`
                  }
                  className="factures-pagination"
                />
              </TableContainer>
            )}
          </section>
        </Container>

        <Dialog
          open={formDialogOpen}
          onClose={() => !saving && setFormDialogOpen(false)}
          fullWidth
          maxWidth="md"
        >
          <DialogTitle>
            {formMode === 'edit' ? 'Modifier la facture' : 'Ajouter une facture'}
          </DialogTitle>

          <DialogContent>
            <Box className="facture-form-grid">
              
              <TextField
                select
                label="Utilisateur attribué"
                value={form.id_utilisateur}
                onChange={(event) =>
                  updateForm(
                    'id_utilisateur',
                    event.target.value
                  )
                }
                fullWidth
                required
                disabled={usersLoading}
                helperText={
                  usersLoading
                    ? 'Chargement des utilisateurs...'
                    : users.length === 0
                      ? 'Aucun compte utilisateur actif n’est disponible.'
                      : 'Cette facture sera visible uniquement par cet utilisateur.'
                }
              >
                <MenuItem value="" disabled>
                  Sélectionner un utilisateur
                </MenuItem>

                {users.map((user) => {
                  const userId =
                    user.id_utilisateur ??
                    user.Id_Utilisateur ??
                    user.id ??
                    user.user_id;

                  const fullName = `${
                    user.prenom ||
                    user.Prenom ||
                    ''
                  } ${
                    user.nom ||
                    user.Nom ||
                    ''
                  }`.trim();

                  const label =
                    fullName ||
                    `Utilisateur ${userId}`;

                  return (
                    <MenuItem
                      key={String(userId)}
                      value={userId}
                    >
                      {label}
                    </MenuItem>
                  );
                })}
              </TextField>

<TextField
                label="Numéro de facture"
                value={form.numero_facture}
                onChange={(e) => updateForm('numero_facture', e.target.value)}
                fullWidth
              />

              <TextField
                label="Nom du client"
                value={form.nom_client}
                onChange={(e) => updateForm('nom_client', e.target.value)}
                fullWidth
                
              />

              <TextField
                label={'\u00c9metteur'}
                value={form.emetteur}
                onChange={(e) => updateForm('emetteur', e.target.value)}
                fullWidth
                
              />

              <TextField
                select
                label="Devise"
                value={form.devise}
                onChange={(e) => updateForm('devise', e.target.value)}
                fullWidth
              >
                <MenuItem value="EUR">Euro (€)</MenuItem>
                <MenuItem value="MAD">Dirham marocain (DH)</MenuItem>
              </TextField>

              <TextField
                label={`Montant HT ${form.devise === 'MAD' ? '(DH)' : '(€)'}`}
                type="number"
                value={form.montant_ht}
                onChange={(e) => updateForm('montant_ht', e.target.value)}
                fullWidth
              />

              <TextField
                label={`Montant TTC ${form.devise === 'MAD' ? '(DH)' : '(€)'}`}
                type="number"
                value={form.montant_ttc}
                onChange={(e) => updateForm('montant_ttc', e.target.value)}
                fullWidth
              />

              <TextField
                label="Date de facture"
                type="date"
                value={form.date_facture}
                onChange={(e) => updateForm('date_facture', e.target.value)}
                fullWidth
                
                InputLabelProps={{ shrink: true }}
              />

              <TextField
                label="Date d’échéance"
                type="date"
                value={form.date_echeance}
                onChange={(e) => updateForm('date_echeance', e.target.value)}
                fullWidth
                required
                InputLabelProps={{ shrink: true }}
              />

              <TextField
                select
                label="Statut"
                value={form.status}
                onChange={(e) => {
                  const nextStatus = e.target.value;

                  updateForm(
                    'status',
                    nextStatus
                  );

                  if (
                    nextStatus !== 'Pay\u00e9e'
                  ) {
                    updateForm(
                      'date_paiement',
                      ''
                    );
                  }
                }}
                fullWidth
              >
                <MenuItem value="Non payée">Non payée</MenuItem>
                <MenuItem value="Payée">Payée</MenuItem>
              </TextField>

              {form.status === 'Pay\u00e9e' && (
                <TextField
                  label="Date de paiement"
                  type="date"
                  value={form.date_paiement}
                  onChange={(e) =>
                    updateForm(
                      'date_paiement',
                      e.target.value
                    )
                  }
                  fullWidth
                  required
                  InputLabelProps={{
                    shrink: true
                  }}
                />
              )}

              <Button variant="outlined" component="label" className="facture-file-picker">
                {scanning ? 'Analyse du PDF...' : formMode === 'edit' ? 'Changer le PDF' : 'Scanner et pr\u00e9remplir le PDF'}
                <input
                  type="file"
                  hidden
                  accept="application/pdf,.pdf"
                  onChange={handlePdfSelected}
                />
              </Button>
            </Box>

            {form.file && (
              <Typography className="facture-selected-file">
                Fichier sélectionné : {form.file.name}
              </Typography>
            )}

            {formMode === 'edit' && !form.file && (
              <Typography className="facture-edit-helper">
                Vous pouvez modifier toutes les informations sans changer le PDF.
              </Typography>
            )}
          </DialogContent>

          <DialogActions>
            <Button variant="outlined" onClick={() => setFormDialogOpen(false)} disabled={saving}>
              Annuler
            </Button>

            <Button variant="contained" onClick={handleSaveFacture} disabled={saving}>
              {saving ? <NovaButtonLoader label="Enregistrement" /> : formMode === 'edit' ? 'Enregistrer les modifications' : 'Ajouter la facture'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={deleteDialogOpen} onClose={() => !isDeleting && setDeleteDialogOpen(false)}>
          <DialogTitle>Supprimer la facture</DialogTitle>
          <DialogContent>
            <Typography>
              {isDeleting ? 'Suppression en cours...' : 'Voulez-vous vraiment supprimer cette facture ?'}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)} disabled={isDeleting}>
              Annuler
            </Button>
            <Button color="error" onClick={handleDeleteFacture} disabled={isDeleting}>
              {isDeleting ? <NovaButtonLoader label="Suppression" /> : 'Supprimer'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={payDialogOpen}
          onClose={() => {
            if (!isUpdatingStatus) {
              setPayDialogOpen(false);
              setPaymentDate('');
              setFactureToToggle(null);
            }
          }}
        >
          <DialogTitle>
            {'Marquer la facture comme pay\u00e9e'}
          </DialogTitle>

          <DialogContent>
            <TextField
              label="Date de paiement"
              type="date"
              value={paymentDate}
              onChange={(e) =>
                setPaymentDate(
                  e.target.value
                )
              }
              fullWidth
              required
              margin="normal"
              InputLabelProps={{
                shrink: true
              }}
            />
          </DialogContent>

          <DialogActions>
            <Button
              onClick={() => {
                setPayDialogOpen(false);
                setPaymentDate('');
                setFactureToToggle(null);
              }}
              disabled={isUpdatingStatus}
            >
              Annuler
            </Button>

            <Button
              variant="contained"
              disabled={
                isUpdatingStatus
                || !paymentDate
              }
              onClick={async () => {
                if (
                  factureToToggle
                  && paymentDate
                ) {
                  await updateStatus(
                    factureToToggle.id_facture,
                    'Pay\u00e9e',
                    paymentDate
                  );

                  setPayDialogOpen(false);
                  setPaymentDate('');
                  setFactureToToggle(null);
                }
              }}
            >
              {isUpdatingStatus
                ? <NovaButtonLoader label={'Mise \u00e0 jour'} />
                : 'Confirmer'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={unpayDialogOpen} onClose={() => !isUpdatingStatus && setUnpayDialogOpen(false)}>
          <DialogTitle>Confirmer</DialogTitle>
          <DialogContent>
            <Typography>Remettre cette facture en « Non payée » ?</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setUnpayDialogOpen(false)} disabled={isUpdatingStatus}>
              Annuler
            </Button>
            <Button
              color="error"
              disabled={isUpdatingStatus}
              onClick={async () => {
                if (factureToToggle) {
                  await updateStatus(factureToToggle.id_facture, 'Non payée');
                  setUnpayDialogOpen(false);
                  setFactureToToggle(null);
                }
              }}
            >
              {isUpdatingStatus ? <NovaButtonLoader label="Mise à jour" /> : 'Confirmer'}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={notification.open}
          autoHideDuration={2800}
          onClose={() => setNotification({ ...notification, open: false })}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert severity={notification.severity} className="factures-snackbar-alert">
            {notification.message}
          </Alert>
        </Snackbar>
      </main>
    </LayoutDashboard>
  );
};

export default FacturePage;
